import { useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";

import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  getEmployee,
  selectProcesses,
  removeEmployee,
  removeEmployeeFromListById,
  createEmployee,
  changeEmployeeStatus,
} from "../process/processSlices";
import { store } from "../../app/store";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import Paper from "@mui/material/Paper";
import { NavLink as RouterLink } from "react-router-dom";

import {
  Chip,
  IconButton,
  LinearProgress,
  Stack,
  Button,
  Typography,
  Tooltip,
  Box,
  Accordion,
  AccordionDetails,
  Avatar,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  useTheme,
  Switch,
  FormControl,
  MenuItem,
  Select,
  TableRow,
  Tab,
  Tabs,
} from "@mui/material";
import type { SelectChangeEvent } from "@mui/material/Select";
import { alpha, keyframes } from "@mui/material/styles";
import {
  AccountBalance,
  Add,
  AirplaneTicket,
  Assignment,
  AssignmentTurnedIn,
  Block,
  BusinessCenter,
  ChevronRight,
  ContactPage,
  Delete,
  Edit,
  ExpandMore,
  FilterList,
  Flight,
  FlightTakeoff,
  HourglassEmpty,
  HowToReg,
  NextPlan,
  PauseCircle,
  PeopleOutline,
  Print,
  Refresh,
  Remove,
  RemoveRedEye,
  Restore,
  SelectAll,
  ThumbDown,
  ThumbUp,
} from "@mui/icons-material";

import { StyledTableCell, StyledTableRow } from "../../styles/tableStyles";
import TableSkeleton from "../../components/Layout/TableSkeleton";
import Paging from "../../components/Layout/Paging";
import Toast from "../../components/Layout/Toast";
import { useTranslation } from "react-i18next";
import { selectAuth } from "../auth/authSlice";
import { AuthUser, ClientTypes, Role } from "../auth/types/authType";
import { RoleTypes } from "../auth/types/roleTypes";
import {
  CompletionStatus,
  CountriesList,
  Employee,
  EmployeeStatus,
  GulfCountries,
  Nationalities,
  NotificationMessageTypes,
} from "./types/processTypes";
import { Company } from "../setups/types/setupTypes";
import { isPrivilegedTransaction } from "../../utils/authUtils";
import { ProcessStages } from "./components/ProcessStages";
import { useEmployeeListFetch } from "./hooks/useEmployeeListFetch";
import CustomDialog from "../../components/modals/CustomDialog";
import { VisaEntry } from "./VisaEntry";
import { CompanyFilter } from "../../components/filter/CompanyFilter";
import AuthSkeleton from "../auth/AuthSkeleton";
import Search from "../../components/search/Search";
import {
  selectPreference,
  setAppliedCountry,
  toggleThis,
} from "../preferences/preferencesSlice";
import FilterSidebar from "../../components/filter/FilterSidebar";
import ExportToExcel from "./components/ExportToExcel";
import { formatReferenceNumber } from "./utils/referenceNumber";
import { addDays, format } from "date-fns";
import { EmbassyList } from "../../components/reports/embassy/EmbassyList";
import { pdf } from "@react-pdf/renderer";
import { StyledAccordionSummary } from "../../styles/componentStyled";
import { apolloClient } from "../../apollo/graphql";
import { GET_EMPLOYEE_DASHBOARD_STATS } from "../../apollo/queries/processes";

/** Matches `Dashboard.tsx` — aggregates API rows into canonical enum buckets. */
const DASHBOARD_STATUS_ORDER: EmployeeStatus[] = [
  EmployeeStatus.pending,
  EmployeeStatus.new,
  EmployeeStatus.selected,
  EmployeeStatus.contracted,
  EmployeeStatus.visa_issued,
  EmployeeStatus.labour_issued,
  EmployeeStatus.flight_booked,
  EmployeeStatus.departed,
  EmployeeStatus.discontinued,
  EmployeeStatus.stopped,
  EmployeeStatus.lost,
  EmployeeStatus.returned,
  EmployeeStatus.has_complain,
  EmployeeStatus.contract_completed,
  EmployeeStatus.other,
];

const DASHBOARD_STATUS_SET = new Set<string>(DASHBOARD_STATUS_ORDER);

function normalizeEmployeeStatusForDashboard(
  status: string | undefined | null,
): EmployeeStatus {
  if (status && DASHBOARD_STATUS_SET.has(status)) {
    return status as EmployeeStatus;
  }
  return EmployeeStatus.other;
}

function sumDashboardCountsForStatuses(
  countsByStatus: Record<string, number>,
  statuses: EmployeeStatus[],
): number {
  return statuses.reduce((sum, s) => sum + (countsByStatus[s] ?? 0), 0);
}

const employeeTabPanelEnter = keyframes`
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const refreshSpin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

type EmployeeStatusTabValue =
  | "pendingInfo"
  | "available"
  | "selected"
  | "contracted"
  | "embassyProcess"
  | "lmisProcess"
  | "ticketing"
  | "flightBooked"
  | "departed"
  | "discontinuedStatuses"
  | "stoppedStatuses";

const TAB_ICONS: Record<EmployeeStatusTabValue, typeof PeopleOutline> = {
  pendingInfo: HourglassEmpty,
  available: PeopleOutline,
  selected: HowToReg,
  contracted: AssignmentTurnedIn,
  embassyProcess: AccountBalance,
  lmisProcess: BusinessCenter,
  ticketing: AirplaneTicket,
  flightBooked: Flight,
  departed: FlightTakeoff,
  discontinuedStatuses: Block,
  stoppedStatuses: PauseCircle,
};

const EMPLOYEE_STATUS_TABS: {
  value: EmployeeStatusTabValue;
  labelKey: string;
  statuses: EmployeeStatus[];
  /** When set, passed to `fetchEmployees` (server treats `false` as not ticked, including null/missing). */
  visaIssued?: boolean;
  labourIssued?: boolean;
}[] = [
  {
    value: "pendingInfo",
    labelKey: "processTabs.pendingInfo",
    statuses: [EmployeeStatus.pending],
  },
  {
    value: "available",
    labelKey: "processTabs.available",
    statuses: [EmployeeStatus.new],
  },
  {
    value: "selected",
    labelKey: "processTabs.selected",
    statuses: [EmployeeStatus.selected],
  },
  {
    value: "contracted",
    labelKey: "processTabs.contracted",
    statuses: [EmployeeStatus.contracted],
  },
  {
    value: "embassyProcess",
    labelKey: "processTabs.embassyProcess",
    statuses: [EmployeeStatus.contracted, EmployeeStatus.labour_issued],
    visaIssued: false,
  },
  {
    value: "lmisProcess",
    labelKey: "processTabs.lmisProcess",
    statuses: [EmployeeStatus.contracted, EmployeeStatus.visa_issued],
    labourIssued: false,
  },
  {
    value: "ticketing",
    labelKey: "processTabs.ticketing",
    statuses: [EmployeeStatus.visa_issued, EmployeeStatus.labour_issued],
    visaIssued: true,
    labourIssued: true,
  },
  {
    value: "flightBooked",
    labelKey: "processTabs.flightBooked",
    statuses: [EmployeeStatus.flight_booked],
  },
  {
    value: "departed",
    labelKey: "processTabs.departed",
    statuses: [EmployeeStatus.departed],
  },
  {
    value: "discontinuedStatuses",
    labelKey: "processTabs.discontinuedStatuses",
    statuses: [EmployeeStatus.discontinued],
  },
  {
    value: "stoppedStatuses",
    labelKey: "processTabs.stoppedStatuses",
    statuses: [EmployeeStatus.stopped],
  },
];

/** Narrow roles that grant seeing a pipeline tab and opening ProcessStages from the list (plus `ChangeEmployeeStatus`). */
const EMPLOYEE_TAB_PROCESS_PRIVILEGES: Record<
  EmployeeStatusTabValue,
  RoleTypes[]
> = {
  pendingInfo: [
    RoleTypes.RegisterCandidate,
    RoleTypes.SelectCandidates,
    RoleTypes.AddMedical,
    RoleTypes.AddCoc,
  ],
  available: [
    RoleTypes.SelectCandidates,
    RoleTypes.AddMedical,
    RoleTypes.AddCoc,
  ],
  selected: [
    RoleTypes.SelectCandidates,
    RoleTypes.ContractEmployees,
    RoleTypes.AddMedical,
    RoleTypes.AddCoc,
  ],
  contracted: [
    RoleTypes.SelectCandidates,
    RoleTypes.ContractEmployees,
    RoleTypes.AddMedical,
    RoleTypes.AddCoc,
  ],
  embassyProcess: [RoleTypes.IssueEmployeeVisa],
  lmisProcess: [RoleTypes.IssueEmployeeLabour],
  ticketing: [RoleTypes.BookEmployeeTicket],
  flightBooked: [RoleTypes.BookEmployeeTicket, RoleTypes.MarkEmployeeDeparted],
  departed: [
    RoleTypes.MarkEmployeeDeparted,
    RoleTypes.ChangeEmployeeDisposition,
  ],
  discontinuedStatuses: [RoleTypes.DiscontinueProcess],
  stoppedStatuses: [RoleTypes.StopProcess],
};

function canOpenProcessStagesForTab(
  roles: Role[] | undefined,
  tab: EmployeeStatusTabValue,
): boolean {
  const r = roles ?? [];
  if (isPrivilegedTransaction(r, RoleTypes.ChangeEmployeeStatus)) {
    return true;
  }
  const privileges = EMPLOYEE_TAB_PROCESS_PRIVILEGES[tab];
  return privileges.some((p) => isPrivilegedTransaction(r, p));
}

type EmployeeTabConfig = (typeof EMPLOYEE_STATUS_TABS)[number];

function tabUsesServerListFilters(tab: EmployeeTabConfig): boolean {
  return tab.visaIssued !== undefined || tab.labourIssued !== undefined;
}

/** Contracted tab: medical and/or COC still open, or medically unfit (may overlap Embassy/LMIS). */
function employeeMatchesContractedPrepTab(emp: Employee): boolean {
  if (emp.status !== EmployeeStatus.contracted) return false;
  return (
    emp.medicalCompleted !== true ||
    emp.cocCompleted !== true ||
    emp.medicalFit === false
  );
}

/** Whether `emp` would be returned by the list query for the given tab (client mirror of server filters). */
function employeeMatchesActiveTab(
  emp: Employee,
  tabValue: EmployeeStatusTabValue,
): boolean {
  const tab = EMPLOYEE_STATUS_TABS.find((t) => t.value === tabValue);
  if (!tab) return true;
  const s = emp.status as EmployeeStatus | undefined;
  if (tab.value === "contracted") {
    return employeeMatchesContractedPrepTab(emp);
  }
  if (tab.value === "embassyProcess") {
    const okStatus =
      s === EmployeeStatus.contracted || s === EmployeeStatus.labour_issued;
    return (
      okStatus &&
      emp.visaIssued !== true &&
      Boolean(emp.medicalCompleted) &&
      emp.medicalFit !== false
    );
  }
  if (tab.value === "lmisProcess") {
    const okStatus =
      s === EmployeeStatus.contracted || s === EmployeeStatus.visa_issued;
    return (
      okStatus &&
      emp.labourIssued !== true &&
      Boolean(emp.cocCompleted) &&
      emp.medicalFit !== false
    );
  }
  if (tab.value === "ticketing") {
    const okStatus =
      s === EmployeeStatus.visa_issued || s === EmployeeStatus.labour_issued;
    return okStatus && emp.visaIssued === true && emp.labourIssued === true;
  }
  if (tab.value === "discontinuedStatuses") {
    return s === EmployeeStatus.discontinued;
  }
  if (tab.value === "stoppedStatuses") {
    return s === EmployeeStatus.stopped;
  }
  if (tab.value === "pendingInfo") {
    return s === EmployeeStatus.pending;
  }
  return tab.statuses.some((st) => st === s);
}

const GULF_COUNTRY_FLAGS: Record<string, string> = {
  saudi_arabia: "🇸🇦",
  uae: "🇦🇪",
  kuwait: "🇰🇼",
  qatar: "🇶🇦",
  jordan: "🇯🇴",
  lebanon: "🇱🇧",
  bahrain: "🇧🇭",
  oman: "🇴🇲",
};

function getStatusChipColor(
  status: string | undefined,
): "default" | "success" | "error" | "warning" | "info" {
  if (!status) return "default";
  if (status === EmployeeStatus.pending) return "warning";
  if (
    status === EmployeeStatus.departed ||
    status === EmployeeStatus.contract_completed
  )
    return "success";
  if (
    status === EmployeeStatus.discontinued ||
    status === EmployeeStatus.stopped ||
    status === EmployeeStatus.lost ||
    status === EmployeeStatus.returned ||
    status === EmployeeStatus.has_complain
  )
    return "error";
  if (
    status === EmployeeStatus.flight_booked ||
    status === EmployeeStatus.visa_issued ||
    status === EmployeeStatus.labour_issued
  )
    return "warning";
  if (
    status === EmployeeStatus.selected ||
    status === EmployeeStatus.contracted
  )
    return "info";
  return "default";
}

export const Employees = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { t: te } = useTranslation("enums");
  const theme = useTheme();
  const {
    employeesWithCount: { employees, totalCount },
    loading: employeesLoading,
    success,
    error,
  } = useAppSelector(selectProcesses);
  const { user } = useAppSelector(selectAuth);
  const {
    searchText,
    status: sidebarStatus,
    nationality,
    appliedCountry,
    addressRegion,
    brokerName,
    maritalStatus,
    religion,
    profession,
    arabic,
    english,
    ageStart,
    ageEnd,
    experienceYears,
    agencyId,
    agentId,
    cocCompleted: coc,
    medicalCompleted: medical,
    biometricsCompleted: biometrics,
  } = useAppSelector(selectPreference);
  const [debouncedSearchText, setDebouncedSearchText] = useState(
    () => searchText ?? "",
  );

  useEffect(() => {
    const id = window.setTimeout(
      () => setDebouncedSearchText(searchText ?? ""),
      400,
    );
    return () => window.clearTimeout(id);
  }, [searchText]);

  const [total, setTotal] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [currentPage, setCurrentPage] = useState(0);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState("");
  const [employeesForFilter, setEmployeesForFilter] = useState<Employee[]>([]);

  const [open, setOpen] = useState(false);
  const [openChangeStatus, setOpenChangeStatus] = useState(false);
  const [openVisa, setOpenVisa] = useState(false);
  const [openAgentSelect, setOpenAgentSelect] = useState(false);
  const [agentSelectEmployee, setAgentSelectEmployee] =
    useState<Employee | null>(null);
  const [agentForSelect, setAgentForSelect] = useState<Company | undefined>();
  const [openStop, setOpenStop] = useState(false);
  const [stopEmployee, setStopEmployee] = useState<Employee | null>(null);

  const [selectedEmployees, setSelectedEmployees] = useState<Employee[]>([]);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee>();

  const [selectAll, setSelectAll] = useState(false);

  const visibleEmployeeTabs = useMemo(() => {
    const roles = user?.roles as Role[] | undefined;
    const filtered = EMPLOYEE_STATUS_TABS.filter((tab) =>
      canOpenProcessStagesForTab(roles, tab.value),
    );
    return filtered.length > 0 ? filtered : EMPLOYEE_STATUS_TABS;
  }, [user?.roles]);

  const [activeStatusTab, setActiveStatusTab] =
    useState<EmployeeStatusTabValue>(() =>
      visibleEmployeeTabs.some((t) => t.value === "available")
        ? "available"
        : visibleEmployeeTabs[0].value,
    );

  useEffect(() => {
    const visibleValues = new Set(visibleEmployeeTabs.map((tab) => tab.value));
    if (!visibleValues.has(activeStatusTab)) {
      setActiveStatusTab(visibleEmployeeTabs[0].value);
    }
  }, [visibleEmployeeTabs, activeStatusTab]);

  useEffect(() => {
    if (!sidebarStatus) return;
    const matchingTab = EMPLOYEE_STATUS_TABS.find((tab) =>
      tab.statuses.includes(sidebarStatus),
    );
    if (
      matchingTab &&
      visibleEmployeeTabs.some((t) => t.value === matchingTab.value)
    ) {
      setActiveStatusTab(matchingTab.value);
    }
  }, [sidebarStatus, visibleEmployeeTabs]);

  /** Global counts per status (same query as Dashboard); used for tab badges. */
  const [statusDashboardCounts, setStatusDashboardCounts] = useState<
    Record<string, number>
  >({});
  const [dashboardStatsLoading, setDashboardStatsLoading] = useState(true);
  /** Bumped when closing the change-status dialog so tab totals / histogram refetch. */
  const [dashboardReloadNonce, setDashboardReloadNonce] = useState(0);

  /** Process-tab totals + flag counts from `employeeDashboardStats` (same filter scope as list, excluding status tab). */
  const [processTabDashboardCounts, setProcessTabDashboardCounts] = useState({
    embassyProcessTabCount: 0,
    lmisProcessTabCount: 0,
    ticketingTabCount: 0,
    visaIssuedTrueCount: 0,
    labourIssuedTrueCount: 0,
    contractedPrepTabCount: 0,
  });

  const dashboardStatsVariables = useMemo(
    () => ({
      searchText:
        debouncedSearchText && debouncedSearchText.length > 0
          ? debouncedSearchText
          : undefined,
      status: sidebarStatus,
      nationality,
      appliedCountry: appliedCountry ?? null,
      region: addressRegion ?? null,
      brokerName: brokerName ?? null,
      maritalStatus,
      religion,
      profession,
      arabic,
      english,
      ageStart,
      ageEnd,
      experienceYears,
      agencyId,
      agentId,
      cocCompleted:
        coc && coc === CompletionStatus.completed
          ? true
          : coc === CompletionStatus.not_completed
            ? false
            : undefined,
      medicalCompleted:
        medical && medical === CompletionStatus.completed
          ? true
          : medical === CompletionStatus.not_completed
            ? false
            : undefined,
      biometricsCompleted:
        biometrics && biometrics === CompletionStatus.completed
          ? true
          : biometrics === CompletionStatus.not_completed
            ? false
            : undefined,
    }),
    [
      debouncedSearchText,
      sidebarStatus,
      nationality,
      appliedCountry,
      addressRegion,
      brokerName,
      maritalStatus,
      religion,
      profession,
      arabic,
      english,
      ageStart,
      ageEnd,
      experienceYears,
      agencyId,
      agentId,
      coc,
      medical,
      biometrics,
    ],
  );

  useEffect(() => {
    let cancelled = false;

    async function loadDashboardTabCounts() {
      setDashboardStatsLoading(true);
      try {
        const { data } = await apolloClient.query({
          query: GET_EMPLOYEE_DASHBOARD_STATS,
          variables: dashboardStatsVariables,
          fetchPolicy: "network-only",
        });
        if (cancelled) return;

        const stats = data?.employeeDashboardStats;
        const groupByCategory: Record<string, number> = {};
        for (const row of stats?.byStatus ?? []) {
          const bucket = normalizeEmployeeStatusForDashboard(row.status);
          groupByCategory[bucket] = (groupByCategory[bucket] ?? 0) + row.count;
        }
        setStatusDashboardCounts(groupByCategory);
        setProcessTabDashboardCounts({
          embassyProcessTabCount: stats?.embassyProcessTabCount ?? 0,
          lmisProcessTabCount: stats?.lmisProcessTabCount ?? 0,
          ticketingTabCount: stats?.ticketingTabCount ?? 0,
          visaIssuedTrueCount: stats?.visaIssuedTrueCount ?? 0,
          labourIssuedTrueCount: stats?.labourIssuedTrueCount ?? 0,
          contractedPrepTabCount: stats?.contractedPrepTabCount ?? 0,
        });
      } finally {
        if (!cancelled) {
          setDashboardStatsLoading(false);
        }
      }
    }

    void loadDashboardTabCounts();
    return () => {
      cancelled = true;
    };
  }, [dashboardStatsVariables, dashboardReloadNonce]);

  const tabBadgeCountLabel = useCallback(
    (tab: EmployeeTabConfig) => {
      if (dashboardStatsLoading) return "…";
      if (tab.value === "contracted") {
        return String(processTabDashboardCounts.contractedPrepTabCount);
      }
      if (tabUsesServerListFilters(tab)) {
        if (tab.value === "embassyProcess") {
          return String(processTabDashboardCounts.embassyProcessTabCount);
        }
        if (tab.value === "lmisProcess") {
          return String(processTabDashboardCounts.lmisProcessTabCount);
        }
        if (tab.value === "ticketing") {
          return String(processTabDashboardCounts.ticketingTabCount);
        }
        return "0";
      }
      return String(
        sumDashboardCountsForStatuses(statusDashboardCounts, tab.statuses),
      );
    },
    [dashboardStatsLoading, processTabDashboardCounts, statusDashboardCounts],
  );

  const handleClickOpen = (id: string) => {
    setSelectedEmployeeId(id);
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };
  const DeleteEmployee = () => {
    setOpen(false);
    dispatch(removeEmployee(selectedEmployeeId));
  };

  const changeCandidateStatus = async (employee: Employee) => {
    if (employee._id) {
      const result = await dispatch(getEmployee(String(employee._id)));
      if (getEmployee.fulfilled.match(result)) {
        setSelectedEmployee(result.payload);
        setOpenChangeStatus(true);
        return;
      }
    }
    setSelectedEmployee(employee);
    setOpenChangeStatus(true);
  };
  const selectCandidateForVisa = async (employee: Employee) => {
    const result = await dispatch(getEmployee(String(employee._id)));
    if (getEmployee.fulfilled.match(result)) {
      setSelectedEmployee(result.payload);
    } else {
      setSelectedEmployee(employee);
    }
    setOpenVisa(true);
  };

  const openAgentSelectDialog = (employee: Employee) => {
    setAgentSelectEmployee(employee);
    setOpenAgentSelect(true);
  };

  const listFetchParams = useMemo(() => {
    const skipRows = currentPage * rowsPerPage;

    const tabConfig = EMPLOYEE_STATUS_TABS.find(
      (tab) => tab.value === activeStatusTab,
    );
    const tabStatuses = tabConfig?.statuses ?? [EmployeeStatus.new];
    const tabStatusFilter =
      tabStatuses.length > 1
        ? { status: undefined, statuses: tabStatuses }
        : { status: tabStatuses[0], statuses: undefined };
    const effectiveStatusFilter = sidebarStatus
      ? { status: sidebarStatus, statuses: undefined }
      : tabStatusFilter;

    let cocCompleted: boolean | undefined =
      coc && coc === CompletionStatus.completed
        ? true
        : coc === CompletionStatus.not_completed
          ? false
          : undefined;
    let medicalCompleted: boolean | undefined =
      medical && medical === CompletionStatus.completed
        ? true
        : medical === CompletionStatus.not_completed
          ? false
          : undefined;

    if (activeStatusTab === "embassyProcess") {
      medicalCompleted =
        medical === CompletionStatus.not_completed ? false : true;
    }
    if (activeStatusTab === "lmisProcess") {
      cocCompleted = coc === CompletionStatus.not_completed ? false : true;
    }
    return {
      skip: skipRows,
      take: rowsPerPage,
      searchText:
        debouncedSearchText && debouncedSearchText.length > 0
          ? debouncedSearchText
          : undefined,
      ...effectiveStatusFilter,
      ...(tabConfig?.visaIssued !== undefined
        ? { visaIssued: tabConfig.visaIssued }
        : {}),
      ...(tabConfig?.labourIssued !== undefined
        ? { labourIssued: tabConfig.labourIssued }
        : {}),
      ...(activeStatusTab === "contracted" ? { contractedPrep: true } : {}),
      ...(activeStatusTab === "embassyProcess" ? { embassyPrep: true } : {}),
      ...(activeStatusTab === "lmisProcess" ? { lmisPrep: true } : {}),
      nationality,
      appliedCountry,
      region: addressRegion,
      brokerName,
      maritalStatus,
      religion,
      profession,
      arabic,
      english,
      ageStart,
      ageEnd,
      experienceYears,
      agencyId,
      agentId,
      cocCompleted,
      medicalCompleted,
      biometricsCompleted:
        biometrics && biometrics === CompletionStatus.completed
          ? true
          : biometrics === CompletionStatus.not_completed
            ? false
            : undefined,
    };
  }, [
    debouncedSearchText,
    activeStatusTab,
    sidebarStatus,
    nationality,
    appliedCountry,
    addressRegion,
    brokerName,
    maritalStatus,
    religion,
    profession,
    arabic,
    english,
    ageStart,
    ageEnd,
    experienceYears,
    currentPage,
    rowsPerPage,
    agencyId,
    agentId,
    coc,
    medical,
    biometrics,
  ]);

  const { refreshList } = useEmployeeListFetch(listFetchParams);

  const refreshEmployeeData = useCallback(() => {
    refreshList();
    setDashboardReloadNonce((n) => n + 1);
  }, [refreshList]);

  const handleRefresh = useCallback(() => {
    refreshEmployeeData();
  }, [refreshEmployeeData]);

  const dialogCloseChangeStatus = useCallback(() => {
    setOpenChangeStatus(false);
    refreshEmployeeData();

    const emp = store.getState().processes.selectedEmployee as
      | Employee
      | undefined;
    if (!emp?._id) return;
    if (!employeeMatchesActiveTab(emp, activeStatusTab)) {
      dispatch(removeEmployeeFromListById(String(emp._id)));
      setSelectedEmployees((prev) =>
        prev.filter((e) => e._id == null || String(e._id) !== String(emp._id)),
      );
    }
  }, [activeStatusTab, dispatch, refreshEmployeeData]);

  const dialogCloseVisa = useCallback(() => {
    setOpenVisa(false);
    refreshEmployeeData();

    const emp = store.getState().processes.selectedEmployee as
      | Employee
      | undefined;
    if (!emp?._id) return;
    if (!employeeMatchesActiveTab(emp, activeStatusTab)) {
      dispatch(removeEmployeeFromListById(String(emp._id)));
      setSelectedEmployees((prev) =>
        prev.filter((e) => e._id == null || String(e._id) !== String(emp._id)),
      );
    }
  }, [activeStatusTab, dispatch, refreshEmployeeData]);

  const handleStopEmployee = async () => {
    if (!stopEmployee?._id) return;
    const stoppedId = String(stopEmployee._id);
    const result = await dispatch(
      changeEmployeeStatus({
        _id: stoppedId,
        status: EmployeeStatus.stopped,
        currentStatusDate: new Date(),
      }),
    );
    setOpenStop(false);
    setStopEmployee(null);
    if (changeEmployeeStatus.fulfilled.match(result)) {
      refreshEmployeeData();
      if (!employeeMatchesActiveTab(result.payload, activeStatusTab)) {
        dispatch(removeEmployeeFromListById(stoppedId));
        setSelectedEmployees((prev) =>
          prev.filter((e) => e._id == null || String(e._id) !== stoppedId),
        );
      }
    }
  };

  const restoreToAvailable = async (employee: Employee) => {
    const result = await dispatch(
      changeEmployeeStatus({
        _id: String(employee._id),
        status: EmployeeStatus.new,
        currentStatusDate: new Date(),
      }),
    );
    if (changeEmployeeStatus.fulfilled.match(result)) {
      refreshEmployeeData();
      if (!employeeMatchesActiveTab(result.payload, activeStatusTab)) {
        dispatch(removeEmployeeFromListById(String(employee._id)));
        setSelectedEmployees((prev) =>
          prev.filter(
            (e) => e._id == null || String(e._id) !== String(employee._id),
          ),
        );
      }
    }
  };

  const justSelect = async () => {
    if (!agentSelectEmployee) return;
    const values: Employee = {
      ...agentSelectEmployee,
      status: EmployeeStatus.selected,
      notificationMessage: NotificationMessageTypes.selected as string,
      partner: agentForSelect,
      selectionDueDate: addDays(new Date(), 21),
    };
    const createResult = await dispatch(createEmployee(values));
    setOpenAgentSelect(false);
    setAgentSelectEmployee(null);
    if (createEmployee.fulfilled.match(createResult)) {
      refreshEmployeeData();
      if (!employeeMatchesActiveTab(createResult.payload, activeStatusTab)) {
        dispatch(removeEmployeeFromListById(String(createResult.payload._id)));
        setSelectedEmployees((prev) =>
          prev.filter(
            (e) =>
              e._id == null ||
              String(e._id) !== String(createResult.payload._id),
          ),
        );
      }
    }
  };

  const deSelectCandidate = async (employee: Employee) => {
    const result = await dispatch(getEmployee(String(employee._id)));
    const base = getEmployee.fulfilled.match(result)
      ? (result.payload as Employee)
      : employee;
    const values: Employee = {
      ...base,
      status: EmployeeStatus.new,
      notificationMessage: NotificationMessageTypes.deSelected as string,
      clearVisa: true,
      selectionDueDate: new Date(),
    };
    const createResult = await dispatch(createEmployee(values));
    if (createEmployee.fulfilled.match(createResult)) {
      refreshEmployeeData();
      if (!employeeMatchesActiveTab(createResult.payload, activeStatusTab)) {
        dispatch(removeEmployeeFromListById(String(employee._id)));
        setSelectedEmployees((prev) =>
          prev.filter(
            (e) => e._id == null || String(e._id) !== String(employee._id),
          ),
        );
      }
    }
  };

  async function printEmbassyList() {
    if (!selectedEmployees.length) return;
    try {
      const doc = EmbassyList({
        employees: selectedEmployees,
        user: user as AuthUser,
      });
      const blob = await pdf(doc).toBlob();
      const url = URL.createObjectURL(blob);
      const iframe = document.createElement("iframe");
      iframe.style.display = "none";
      document.body.appendChild(iframe);
      iframe.src = url;
      iframe.onload = function () {
        setTimeout(function () {
          iframe.focus();
          iframe.contentWindow?.print();
          setTimeout(() => {
            document.body.removeChild(iframe);
            URL.revokeObjectURL(url);
          }, 1000);
        }, 1);
      };
    } catch (e) {
      console.error(e);
    }
  }

  //function filterList() {
  //setEmployeesForFilter(getFilteredList);
  //}
  const handleDrawerOpen = () => {
    dispatch(toggleThis({ type: "Filter", newValue: true }));
  };
  function getDisplayName(company?: Company) {
    if (company) {
      if (theme.direction === "ltr")
        return company.displayName.length > 10
          ? company.displayName.substring(0, company.displayName.indexOf(" "))
          : company.displayName;
      else return company.nameAmharic;
    } else return "";
  }

  useEffect(() => {
    if (employees.length > 0) {
      const selectedIds = new Set(selectedEmployees.map((e) => String(e._id)));
      setEmployeesForFilter(
        employees
          .slice(0, rowsPerPage)
          .filter((e) => !selectedIds.has(String(e._id))),
      );
    } else {
      setEmployeesForFilter([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employees, selectedEmployees]);

  useEffect(() => {
    setTotal(totalCount as number);
    // const checkBoxes = [...Array(totalCount)].map(() => {
    //   return false;
    // });
    // setSelectCandidates(checkBoxes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalCount]);

  const changeCheckBox = (id: string) => {
    if (!selectedEmployees.find((em) => em._id === id))
      setSelectedEmployees([
        ...selectedEmployees,
        employeesForFilter.find((em) => em._id === id) as Employee,
      ]);
    setEmployeesForFilter(employeesForFilter.filter((em) => em._id !== id));
  };
  const removeSelected = (id: string) => {
    setEmployeesForFilter([
      ...employeesForFilter,
      selectedEmployees.find((em) => em._id === id) as Employee,
    ]);
    setSelectedEmployees(selectedEmployees.filter((em) => em._id !== id));
  };
  const changeSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedAll = event.target.checked;
    setSelectAll(selectedAll);
    if (selectedAll) {
      setSelectedEmployees(employeesForFilter);
      setEmployeesForFilter([]);
    } else {
      setEmployeesForFilter(selectedEmployees);
      setSelectedEmployees([]);
    }
  };

  const secondaryDark = theme.palette.secondary.dark;
  const secondaryContrast =
    theme.palette.secondary.contrastText ?? theme.palette.common.white;
  const pillInactiveBg =
    theme.palette.mode === "light"
      ? theme.palette.grey[100]
      : alpha(theme.palette.common.white, 0.06);
  const pillInactiveHoverBg =
    theme.palette.mode === "light"
      ? theme.palette.grey[200]
      : alpha(theme.palette.common.white, 0.1);

  const glowHue = theme.palette.secondary.main;
  const pillSelectedGlow =
    theme.palette.mode === "light"
      ? `0 0 0 2px ${alpha(secondaryContrast, 0.35)}, 0 0 18px ${alpha(glowHue, 0.5)}, 0 5px 22px ${alpha(glowHue, 0.32)}`
      : `0 0 0 2px ${alpha(glowHue, 0.45)}, 0 0 22px ${alpha(glowHue, 0.55)}, 0 6px 28px ${alpha(glowHue, 0.38)}`;
  const pillSelectedGlowHover =
    theme.palette.mode === "light"
      ? `0 0 0 2px ${alpha(secondaryContrast, 0.45)}, 0 0 24px ${alpha(glowHue, 0.62)}, 0 7px 28px ${alpha(glowHue, 0.4)}`
      : `0 0 0 2px ${alpha(glowHue, 0.55)}, 0 0 28px ${alpha(glowHue, 0.68)}, 0 8px 34px ${alpha(glowHue, 0.45)}`;

  const pillSelectedSx = {
    bgcolor: secondaryDark,
    color: secondaryContrast,
    borderWidth: 2,
    borderStyle: "solid" as const,
    borderColor: secondaryDark,
    position: "relative" as const,
    zIndex: 1,
    boxShadow: pillSelectedGlow,
    "&:hover": {
      bgcolor: secondaryDark,
      color: secondaryContrast,
      borderColor: secondaryDark,
      filter: "brightness(1.06)",
      boxShadow: pillSelectedGlowHover,
    },
  };

  const pillInactiveSx = {
    color: secondaryDark,
    borderWidth: 2,
    borderStyle: "solid" as const,
    borderColor: secondaryDark,
    bgcolor: pillInactiveBg,
    "&:hover": {
      color: secondaryDark,
      borderColor: secondaryDark,
      bgcolor: pillInactiveHoverBg,
      boxShadow: "none",
    },
  };

  const renderCountryFilterPills = () => (
    <Stack
      direction="row"
      flexWrap="wrap"
      alignItems="center"
      justifyContent="center"
      sx={{
        gap: 0.75,
        width: "100%",
        maxWidth: "100%",
        flex: { xs: "none", md: "0 1 auto" },
        px: { md: 1 },
      }}
    >
      <Button
        size="small"
        variant="outlined"
        disableElevation
        onClick={() => {
          setCurrentPage(0);
          dispatch(setAppliedCountry(undefined));
        }}
        sx={{
          borderRadius: 999,
          px: 1.75,
          py: 0.5,
          minWidth: 0,
          textTransform: "none",
          fontWeight: 800,
          letterSpacing: 0.02,
          boxShadow: "none",
          transition: theme.transitions.create(
            ["background-color", "color", "border-color", "box-shadow"],
            { duration: theme.transitions.duration.short },
          ),
          ...(appliedCountry === undefined ? pillSelectedSx : pillInactiveSx),
        }}
      >
        {t("allCountries")}
      </Button>
      {Object.keys(GulfCountries).map((key) => {
        const selected = appliedCountry === key;
        return (
          <Button
            key={key}
            size="small"
            variant="outlined"
            disableElevation
            onClick={() => {
              setCurrentPage(0);
              dispatch(setAppliedCountry(key as CountriesList));
            }}
            sx={{
              borderRadius: 999,
              px: 1.5,
              py: 0.5,
              minWidth: 0,
              textTransform: "none",
              fontWeight: 800,
              letterSpacing: 0.02,
              boxShadow: "none",
              transition: theme.transitions.create(
                ["background-color", "color", "border-color", "box-shadow"],
                { duration: theme.transitions.duration.short },
              ),
              ...(selected ? pillSelectedSx : pillInactiveSx),
            }}
          >
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <span style={{ fontSize: "1.1rem", lineHeight: 1 }}>
                {GULF_COUNTRY_FLAGS[key]}
              </span>
              <span>{te(key)}</span>
            </Stack>
          </Button>
        );
      })}
    </Stack>
  );

  const showMedicalColumn = activeStatusTab === "contracted";
  const employeeTableColSpan =
    11 +
    (showMedicalColumn ? 1 : 0) +
    (user?.company?.type === ClientTypes.Moderator ? 1 : 0);

  return (
    <>
      <Helmet>
        <title> {`${t("employees")} | ${t("platform")}`}</title>
      </Helmet>
      <Box
        sx={{
          m: { xs: 0, md: 1 },
          px: { xs: 0, md: 2 },
        }}
      >
        <>
          <>
            <Stack spacing={1.25} sx={{ width: "100%" }}>
              {/* Mobile: add + actions on first row */}
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ width: "100%", display: { xs: "flex", md: "none" } }}
              >
                <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
                  {isPrivilegedTransaction(
                    user?.roles as Role[],
                    "AddEmployees",
                  ) && (
                    <Tooltip title="Add New Employee">
                      <Button
                        color="secondary"
                        variant="contained"
                        component={RouterLink}
                        to={`/app/employee/${Math.random()
                          .toString(36)
                          .slice(2, 12)}`}
                      >
                        <Add />
                      </Button>
                    </Tooltip>
                  )}
                </Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  {selectedEmployees && selectedEmployees.length > 0 && (
                    <>
                      <ExportToExcel selectedEmployees={selectedEmployees} />
                      <Tooltip title="Print Embassy List">
                        <Button
                          color="warning"
                          variant="contained"
                          onClick={printEmbassyList}
                          size="large"
                        >
                          <Print />
                        </Button>
                      </Tooltip>
                    </>
                  )}
                  <Tooltip title={t("filter")}>
                    <Button
                      color="secondary"
                      variant="contained"
                      onClick={handleDrawerOpen}
                      size="large"
                    >
                      <FilterList />
                    </Button>
                  </Tooltip>
                </Stack>
              </Stack>

              {/* Desktop: three columns — add | centered pills | actions */}
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{
                  width: "100%",
                  display: { xs: "none", md: "flex" },
                  columnGap: 1,
                }}
              >
                <Box
                  sx={{
                    flex: "1 1 0",
                    minWidth: 0,
                    display: "flex",
                    justifyContent: "flex-start",
                    alignItems: "center",
                  }}
                >
                  {isPrivilegedTransaction(
                    user?.roles as Role[],
                    "AddEmployees",
                  ) && (
                    <Tooltip title="Add New Employee">
                      <Button
                        color="secondary"
                        variant="contained"
                        component={RouterLink}
                        to={`/app/employee/${Math.random()
                          .toString(36)
                          .slice(2, 12)}`}
                      >
                        <Add />
                      </Button>
                    </Tooltip>
                  )}
                </Box>
                {renderCountryFilterPills()}
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{
                    flex: "1 1 0",
                    minWidth: 0,
                    justifyContent: "flex-end",
                    alignItems: "center",
                  }}
                >
                  {selectedEmployees && selectedEmployees.length > 0 && (
                    <>
                      <ExportToExcel selectedEmployees={selectedEmployees} />
                      <Tooltip title="Print Embassy List">
                        <Button
                          color="warning"
                          variant="contained"
                          onClick={printEmbassyList}
                          size="large"
                        >
                          <Print />
                        </Button>
                      </Tooltip>
                    </>
                  )}
                  <Tooltip title={t("filter")}>
                    <Button
                      color="secondary"
                      variant="contained"
                      onClick={handleDrawerOpen}
                      size="large"
                      sx={{
                        marginRight:
                          theme.direction === "ltr" ? "0px" : "-12px",
                        marginLeft: theme.direction === "rtl" ? "0px" : "-12px",
                      }}
                    >
                      <FilterList />
                    </Button>
                  </Tooltip>
                </Stack>
              </Stack>

              <Box sx={{ width: "100%", display: { xs: "block", md: "none" } }}>
                <FormControl
                  fullWidth
                  size="small"
                  variant="outlined"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: alpha(theme.palette.common.white, 0.97),
                      borderRadius: 2,
                      boxShadow: `0 1px 3px ${alpha(theme.palette.common.black, 0.28)}`,
                      "& fieldset": {
                        borderColor: alpha(theme.palette.common.black, 0.12),
                      },
                      "&:hover fieldset": {
                        borderColor: alpha(theme.palette.secondary.light, 0.9),
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: theme.palette.secondary.main,
                        borderWidth: 2,
                      },
                    },
                    "& .MuiSelect-select": {
                      color: theme.palette.text.primary,
                      fontWeight: 600,
                      py: 1,
                    },
                    "& .MuiSelect-icon": {
                      color: theme.palette.secondary.dark,
                    },
                  }}
                >
                  <Select
                    displayEmpty
                    value={appliedCountry ?? ""}
                    renderValue={(selected) =>
                      selected === "" ? (
                        <Typography
                          component="span"
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontWeight: 600 }}
                        >
                          {t("allCountries")}
                        </Typography>
                      ) : (
                        te(selected)
                      )
                    }
                    MenuProps={{
                      PaperProps: {
                        elevation: 8,
                        sx: {
                          mt: 0.5,
                          borderRadius: 2,
                          maxHeight: 320,
                          "& .MuiMenuItem-root": {
                            typography: "body2",
                          },
                        },
                      },
                    }}
                    onChange={(e: SelectChangeEvent<string>) => {
                      const v = e.target.value;
                      setCurrentPage(0);
                      dispatch(
                        setAppliedCountry(
                          v === "" ? undefined : (v as CountriesList),
                        ),
                      );
                    }}
                  >
                    <MenuItem value="">
                      <em>{t("allCountries")}</em>
                    </MenuItem>
                    {Object.keys(GulfCountries).map((key) => (
                      <MenuItem key={key} value={key}>
                        {te(key)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                }}
              >
                <Tooltip title={t("refresh")}>
                  <span>
                    <Button
                      color="secondary"
                      variant="contained"
                      disableElevation
                      size="small"
                      startIcon={
                        <Refresh
                          sx={{
                            animation:
                              employeesLoading === "pending"
                                ? `${refreshSpin} 0.8s linear infinite`
                                : "none",
                          }}
                        />
                      }
                      onClick={handleRefresh}
                      disabled={employeesLoading === "pending"}
                      sx={{
                        borderRadius: 999,
                        textTransform: "none",
                        fontWeight: 700,
                        px: 2,
                        flexShrink: 0,
                      }}
                    >
                      {t("refresh")}
                    </Button>
                  </span>
                </Tooltip>
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <Box
                    sx={{ width: "100%", maxWidth: { xs: "100%", sm: 560 } }}
                  >
                    <Search initialValue={searchText} />
                  </Box>
                </Box>
              </Box>
            </Stack>
            <Box
              sx={{
                mt: 1.5,
                px: 1,
                py: 0.5,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.primary.dark, 0.85),
              }}
            >
              <Tabs
                value={activeStatusTab}
                onChange={(_, value) => {
                  setActiveStatusTab(value);
                  setCurrentPage(0);
                  setSelectAll(false);
                }}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                textColor="secondary"
                indicatorColor="secondary"
                aria-label="employee status tabs"
                sx={{
                  minHeight: 42,
                  "& .MuiTabs-flexContainer": {
                    justifyContent: "flex-start",
                  },
                  "& .MuiTabs-indicator": {
                    height: 3,
                    borderRadius: 3,
                    backgroundColor: theme.palette.common.white,
                    transition: theme.transitions.create(["left", "width"], {
                      duration: theme.transitions.duration.standard,
                      easing: theme.transitions.easing.easeInOut,
                    }),
                  },
                  "& .MuiTab-root": {
                    minHeight: 42,
                    borderRadius: 1.5,
                    gap: 0.75,
                    textTransform: "none",
                    fontWeight: 700,
                    color: alpha(theme.palette.common.white, 0.72),
                    transition: theme.transitions.create(
                      ["color", "background-color", "transform"],
                      {
                        duration: theme.transitions.duration.short,
                        easing: theme.transitions.easing.easeOut,
                      },
                    ),
                  },
                  "& .MuiTab-root:hover": {
                    color: theme.palette.common.white,
                    backgroundColor: alpha(theme.palette.common.white, 0.12),
                  },
                  "& .MuiTab-root.Mui-selected": {
                    color: theme.palette.common.white,
                    backgroundColor: alpha(theme.palette.common.white, 0.22),
                    transform: "translateY(-1px)",
                  },
                  "& .MuiTabs-scrollButtons": {
                    color: theme.palette.common.white,
                    opacity: 1,
                    width: 32,
                    "&.Mui-disabled": {
                      opacity: 0.2,
                    },
                  },
                }}
              >
                {/* Embassy/LMIS/Ticketing use server tab totals; Contracted prep uses server; other tabs use status histogram. */}
                {visibleEmployeeTabs.map((tab, index) => {
                  const TabIcon = TAB_ICONS[tab.value];
                  return (
                    <Tab
                      key={tab.value}
                      value={tab.value}
                      icon={<TabIcon sx={{ fontSize: "1.125rem" }} />}
                      iconPosition="start"
                      label={
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={0.25}
                        >
                          <span>{`${t(tab.labelKey)} (${tabBadgeCountLabel(tab)})`}</span>
                          {index < visibleEmployeeTabs.length - 1 && (
                            <ChevronRight
                              sx={{ fontSize: "1rem", opacity: 0.4 }}
                            />
                          )}
                        </Stack>
                      }
                    />
                  );
                })}
              </Tabs>
            </Box>
            {selectedEmployees && selectedEmployees.length > 0 && (
              <Accordion expanded>
                <StyledAccordionSummary
                  expandIcon={<ExpandMore />}
                  aria-controls="panel1a-content"
                  id="panel1a-header"
                >
                  <SelectAll />
                  <Typography variant="h6" component="div">
                    Selected Employees
                  </Typography>
                </StyledAccordionSummary>

                <AccordionDetails>
                  <TableContainer component={Paper}>
                    <Table size="small" aria-label="a dense table">
                      <TableHead>
                        <StyledTableRow>
                          <StyledTableCell>Deselect</StyledTableCell>
                          <StyledTableCell>S.No</StyledTableCell>
                          <StyledTableCell>Photo</StyledTableCell>
                          <StyledTableCell>Name</StyledTableCell>
                          <StyledTableCell>Passport No.</StyledTableCell>

                          <StyledTableCell>Visa No.</StyledTableCell>
                          {/* <StyledTableCell>Employer</StyledTableCell> */}
                          <StyledTableCell>Application No.</StyledTableCell>
                        </StyledTableRow>
                      </TableHead>
                      <TableBody>
                        {selectedEmployees.map((row, index) => (
                          <TableRow key={row._id}>
                            <StyledTableCell width="2%">
                              <Tooltip title="Deselect">
                                <IconButton
                                  color="secondary"
                                  onClick={() =>
                                    removeSelected(row._id as string)
                                  }
                                  size="large"
                                >
                                  <Remove />
                                </IconButton>
                              </Tooltip>
                            </StyledTableCell>
                            <StyledTableCell
                              width="2%"
                              sx={{
                                color: theme.palette.primary.main,
                                fontWeight: 900,
                              }}
                              scope="row"
                            >
                              {currentPage * rowsPerPage + index + 1}
                            </StyledTableCell>
                            <StyledTableCell width="4%">
                              <Avatar
                                component={RouterLink}
                                alt="avatar"
                                src={row ? row.facePhoto : ""}
                                sx={{
                                  cursor: "pointer",
                                  height: 30,
                                  width: 30,
                                }}
                                to={`/app/employee/${row._id}`}
                              />
                            </StyledTableCell>
                            <StyledTableCell
                              sx={{
                                color: theme.palette.primary.main,
                                fontWeight: 900,
                              }}
                            >
                              {row.givenName + " " + row.surName}
                            </StyledTableCell>
                            <StyledTableCell
                              sx={{
                                color: theme.palette.primary.main,
                                fontWeight: 900,
                              }}
                            >
                              {row.passportNumber}
                            </StyledTableCell>

                            <StyledTableCell
                              sx={{
                                color: theme.palette.primary.main,
                                fontWeight: 900,
                              }}
                            >
                              {row.visa?.visaNumber}
                            </StyledTableCell>

                            {/* <StyledTableCell
                              sx={{
                                color: theme.palette.primary.main,
                                fontWeight: 900,
                              }}
                            >
                              {row.visa?.passportNumber && row.visa?.fullName
                                ? `${row.visa?.passportNumber} - ${row.visa?.fullName}`
                                : ""}
                            </StyledTableCell> */}
                            <StyledTableCell
                              sx={{
                                color: theme.palette.primary.main,
                                fontWeight: 900,
                              }}
                            >
                              {row.embassyProcess &&
                              row.embassyProcess.enjazNumber
                                ? `${row.embassyProcess.enjazNumber}`
                                : ""}
                            </StyledTableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            )}
          </>

          <Stack
            direction="row"
            spacing={1}
            alignItems="flex-start"
            sx={{ width: "100%" }}
          >
            <Box
              key={activeStatusTab}
              sx={{
                flex: 1,
                minWidth: 0,
                animation: `${employeeTabPanelEnter} ${theme.transitions.duration.standard}ms ${theme.transitions.easing.easeOut} both`,
              }}
            >
              <Box sx={{ display: { xs: "block", md: "none" } }}>
                {employeesLoading === "pending" ? (
                  <AuthSkeleton />
                ) : employeesForFilter.length === 0 ? (
                  <Stack
                    alignItems="center"
                    spacing={1.5}
                    sx={{ py: 6, opacity: 0.55 }}
                  >
                    <PeopleOutline
                      sx={{ fontSize: 52, color: "text.disabled" }}
                    />
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      fontWeight={500}
                    >
                      {t("no_data_found")}
                    </Typography>
                  </Stack>
                ) : (
                  employeesForFilter.map((row, index) => (
                    <TableContainer
                      component={Paper}
                      key={index}
                      sx={{
                        backgroundColor: "white",
                        borderRadius: "4px",
                        px: 1.5,
                        pt: 1,
                        mt: 1,
                      }}
                    >
                      <Table size="medium" aria-label="simple table">
                        <TableBody>
                          <StyledTableRow>
                            <StyledTableCell></StyledTableCell>
                            <StyledTableCell sx={{ textAlign: "right" }}>
                              {" "}
                              <Typography
                                variant="h5"
                                color="primary"
                                alignSelf="flex-end"
                              >
                                {te(row.status as string)}
                              </Typography>{" "}
                            </StyledTableCell>
                          </StyledTableRow>
                          <StyledTableRow>
                            <StyledTableCell></StyledTableCell>
                            <StyledTableCell>
                              <Stack
                                direction="row"
                                justifyContent="flex-start"
                                alignItems="center"
                                spacing={1}
                              >
                                <Avatar
                                  component={RouterLink}
                                  alt="avatar"
                                  src={row ? row.facePhoto : ""}
                                  sx={{
                                    cursor: "pointer",
                                    height: 50,
                                    width: 50,
                                  }}
                                  to={
                                    user?.company?.type ===
                                    ClientTypes.LocalAgency
                                      ? `/app/employee/${row._id}`
                                      : `/app/cvp/${row._id}`
                                  }
                                />
                                <Stack direction="row" spacing={1}>
                                  {row.additionalData?.nationality ===
                                  Nationalities.ethiopian ? (
                                    <img
                                      alt="ethioFlag"
                                      height="20px"
                                      width="30px"
                                      src="/ethiopiaFlag.png"
                                    />
                                  ) : row.additionalData?.nationality ===
                                    Nationalities.kenyan ? (
                                    <img
                                      alt="kenyaFlag"
                                      height="20px"
                                      width="30px"
                                      src="/kenyaFlag.png"
                                    />
                                  ) : row.additionalData?.nationality ===
                                    Nationalities.ugandan ? (
                                    <img
                                      alt="ugandaFlag"
                                      height="20px"
                                      width="30px"
                                      src="/ugandaFlag.png"
                                    />
                                  ) : (
                                    <img
                                      alt="philippineFlag"
                                      height="20px"
                                      width="30px"
                                      src="/philippineFlag.png"
                                    />
                                  )}
                                  <Typography
                                    variant="h5"
                                    color="secondary"
                                    textTransform="capitalize"
                                  >
                                    {te(
                                      row.additionalData?.nationality as string,
                                    )}
                                  </Typography>
                                </Stack>{" "}
                              </Stack>
                            </StyledTableCell>
                          </StyledTableRow>
                          <StyledTableRow>
                            <StyledTableCell>
                              <Typography color="primary" variant="h5">
                                {t("name")}
                              </Typography>
                            </StyledTableCell>
                            <StyledTableCell>
                              {" "}
                              <Typography color="secondary" variant="h5">
                                {row.givenName + " - " + row.passportNumber}
                              </Typography>
                            </StyledTableCell>
                          </StyledTableRow>
                          <StyledTableRow>
                            <StyledTableCell>
                              <>
                                <Typography color="primary" variant="h5">
                                  {t("epro")}
                                </Typography>
                                <Typography color="primary" variant="h5">
                                  {t("pro")}
                                </Typography>
                              </>
                            </StyledTableCell>
                            <StyledTableCell>
                              {" "}
                              {user?.company?.type === ClientTypes.Moderator ? (
                                <>
                                  <Typography
                                    variant="h5"
                                    color="secondary"
                                    textTransform="capitalize"
                                  >
                                    {getDisplayName(row.owner)}
                                  </Typography>

                                  <Typography
                                    variant="h5"
                                    color="secondary"
                                    textTransform="capitalize"
                                  >
                                    {getDisplayName(row.partner)}
                                  </Typography>
                                </>
                              ) : (
                                <Typography
                                  variant="h5"
                                  color="secondary"
                                  textTransform="capitalize"
                                >
                                  {user?.company &&
                                  user.company.type === ClientTypes.ForeignAgent
                                    ? getDisplayName(row.owner)
                                    : getDisplayName(row.partner)}
                                </Typography>
                              )}
                            </StyledTableCell>
                          </StyledTableRow>
                          <StyledTableRow>
                            <StyledTableCell>
                              <Typography color="primary" variant="h5">
                                {t("visa_number")}
                              </Typography>
                            </StyledTableCell>
                            <StyledTableCell>
                              {" "}
                              <Typography variant="h5" color="secondary">
                                {row.visa?.visaNumber}
                              </Typography>
                            </StyledTableCell>
                          </StyledTableRow>
                          <StyledTableRow>
                            <StyledTableCell>
                              <Typography color="primary" variant="h5">
                                {t("employer")}
                              </Typography>
                            </StyledTableCell>
                            <StyledTableCell>
                              <Typography variant="h5" color="secondary">
                                {row.visa?.passportNumber && row.visa?.fullName
                                  ? `${row.visa?.passportNumber} - ${row.visa?.fullName}`
                                  : ""}
                              </Typography>{" "}
                            </StyledTableCell>
                          </StyledTableRow>
                          <StyledTableRow>
                            <StyledTableCell></StyledTableCell>
                            <StyledTableCell>
                              <Stack direction="row" justifyContent="flex-end">
                                {(activeStatusTab === "available" ||
                                  activeStatusTab === "selected") &&
                                  isPrivilegedTransaction(
                                    user?.roles as Role[],
                                    "SelectCandidates",
                                  ) && (
                                    <>
                                      {row.status === EmployeeStatus.new && (
                                        <Tooltip title={t("only_select")}>
                                          <IconButton
                                            color="secondary"
                                            onClick={() =>
                                              openAgentSelectDialog(row)
                                            }
                                            size="large"
                                          >
                                            <ThumbUp />
                                          </IconButton>
                                        </Tooltip>
                                      )}
                                      <Tooltip title={t("addAgreement")}>
                                        <IconButton
                                          color="secondary"
                                          onClick={() =>
                                            selectCandidateForVisa(row)
                                          }
                                          size="large"
                                        >
                                          <Assignment />
                                        </IconButton>
                                      </Tooltip>
                                    </>
                                  )}
                                {(activeStatusTab === "available" ||
                                  activeStatusTab === "selected") &&
                                  (row.status === EmployeeStatus.selected ||
                                    row.status === EmployeeStatus.contracted) &&
                                  isPrivilegedTransaction(
                                    user?.roles as Role[],
                                    "DeselectCandidates",
                                  ) && (
                                    <Tooltip title={t("deselect")}>
                                      <IconButton
                                        color="secondary"
                                        onClick={() => deSelectCandidate(row)}
                                        size="large"
                                      >
                                        <ThumbDown />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                {activeStatusTab !== "available" &&
                                  activeStatusTab !== "selected" &&
                                  activeStatusTab !== "stoppedStatuses" &&
                                  canOpenProcessStagesForTab(
                                    user?.roles as Role[],
                                    activeStatusTab,
                                  ) && (
                                    <Tooltip title={t("status")}>
                                      <IconButton
                                        color="secondary"
                                        onClick={() =>
                                          changeCandidateStatus(row)
                                        }
                                        size="large"
                                      >
                                        <NextPlan />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                {activeStatusTab === "stoppedStatuses" &&
                                  isPrivilegedTransaction(
                                    user?.roles as Role[],
                                    "StopProcess",
                                  ) && (
                                    <Tooltip title={t("restoreToAvailable")}>
                                      <IconButton
                                        color="success"
                                        onClick={() => restoreToAvailable(row)}
                                        size="large"
                                      >
                                        <Restore />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                {isPrivilegedTransaction(
                                  user?.roles as Role[],
                                  "ViewEmployees",
                                ) && (
                                  <Tooltip title="Employee Detail">
                                    <IconButton
                                      color="primary"
                                      component={RouterLink}
                                      to={`/app/employee-detail/${row._id}`}
                                      size="large"
                                    >
                                      <ContactPage />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                {isPrivilegedTransaction(
                                  user?.roles as Role[],
                                  "ViewEmployees",
                                ) && (
                                  <Tooltip title={t("view") + " " + t("cv")}>
                                    <IconButton
                                      color="primary"
                                      component={RouterLink}
                                      to={`/app/cvp/${row._id}`}
                                      size="large"
                                    >
                                      <RemoveRedEye />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                {(row.status === EmployeeStatus.pending ||
                                  row.status === EmployeeStatus.new ||
                                  row.status === EmployeeStatus.selected ||
                                  row.status === EmployeeStatus.contracted) &&
                                  isPrivilegedTransaction(
                                    user?.roles as Role[],
                                    "EditEmployees",
                                  ) && (
                                    <Tooltip title={t("edit")}>
                                      <IconButton
                                        color="primary"
                                        component={RouterLink}
                                        to={`/app/employee/${row._id}`}
                                        size="large"
                                      >
                                        <Edit />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                {(activeStatusTab === "available" ||
                                  activeStatusTab === "pendingInfo") &&
                                  isPrivilegedTransaction(
                                    user?.roles as Role[],
                                    "StopProcess",
                                  ) && (
                                    <Tooltip title={t("stopProcess")}>
                                      <IconButton
                                        color="error"
                                        onClick={() => {
                                          setStopEmployee(row);
                                          setOpenStop(true);
                                        }}
                                        size="large"
                                      >
                                        <PauseCircle />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                {(row.status === EmployeeStatus.new ||
                                  row.status === EmployeeStatus.pending) &&
                                  isPrivilegedTransaction(
                                    user?.roles as Role[],
                                    "DeleteEmployees",
                                  ) && (
                                    <Tooltip title={t("delete")}>
                                      <IconButton
                                        color="secondary"
                                        onClick={() =>
                                          handleClickOpen(row._id as string)
                                        }
                                        size="large"
                                      >
                                        <Delete />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                              </Stack>
                            </StyledTableCell>
                          </StyledTableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ))
                )}
              </Box>
              <TableContainer
                component={Paper}
                sx={{
                  mt: 1,
                  display: { xs: "none", md: "block" },
                  position: "relative",
                }}
              >
                {employeesLoading === "pending" && (
                  <LinearProgress
                    color="secondary"
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      zIndex: 2,
                      height: 3,
                      borderRadius: "4px 4px 0 0",
                    }}
                  />
                )}
                <Table size="medium" aria-label="simple table" stickyHeader>
                  <TableHead>
                    <StyledTableRow>
                      <StyledTableCell width="3%">
                        <Tooltip title="Select All">
                          <Switch
                            checked={selectAll}
                            onChange={(e) => changeSelectAll(e)}
                            inputProps={{ "aria-label": "Select All" }}
                            name="selectAll"
                            color="secondary"
                            size="small"
                          />
                        </Tooltip>
                      </StyledTableCell>
                      <StyledTableCell
                        width="2%"
                        sx={{ textTransform: "uppercase" }}
                      >
                        {t("sno")}
                      </StyledTableCell>
                      <StyledTableCell
                        width="5%"
                        sx={{ textTransform: "uppercase" }}
                      >
                        {t("photo")}
                      </StyledTableCell>
                      <StyledTableCell
                        width="20%"
                        sx={{ textTransform: "uppercase" }}
                      >
                        {t("name")}
                      </StyledTableCell>
                      <StyledTableCell
                        width="10%"
                        sx={{ textTransform: "uppercase" }}
                      >
                        {t("for") + " " + te("country")}
                      </StyledTableCell>
                      <StyledTableCell
                        width="10%"
                        sx={{ textTransform: "uppercase" }}
                      >
                        {t("referenceNumber")}
                      </StyledTableCell>
                      <StyledTableCell
                        width="10%"
                        sx={{ textTransform: "uppercase" }}
                      >
                        {"Labour Id"}
                      </StyledTableCell>
                      {showMedicalColumn && (
                        <StyledTableCell sx={{ textTransform: "uppercase" }}>
                          {te("medical")}
                        </StyledTableCell>
                      )}
                      {user?.company?.type === ClientTypes.Moderator ? (
                        <>
                          <StyledTableCell
                            width="10%"
                            sx={{ textTransform: "uppercase" }}
                          >
                            {t("epro")}
                          </StyledTableCell>
                          <StyledTableCell
                            width="10%"
                            sx={{ textTransform: "uppercase" }}
                          >
                            {t("pro")}
                          </StyledTableCell>
                        </>
                      ) : (
                        <StyledTableCell
                          width="25%"
                          sx={{ textTransform: "uppercase" }}
                        >
                          {t("partner")}
                        </StyledTableCell>
                      )}
                      {/* <StyledTableCell
                    width="5%"
                    sx={{ textTransform: "uppercase" }}
                  >
                    {t("visa_number")}
                  </StyledTableCell> */}
                      {/* <StyledTableCell
                    width="15%"
                    sx={{ textTransform: "uppercase" }}
                  >
                    {t("employer")}
                  </StyledTableCell> */}
                      <StyledTableCell
                        width="10%"
                        sx={{ textTransform: "uppercase" }}
                      >
                        {t("created date")}
                      </StyledTableCell>
                      <StyledTableCell
                        width="5%"
                        sx={{ textTransform: "uppercase" }}
                      >
                        {t("status")}
                      </StyledTableCell>
                      <StyledTableCell
                        width="20%"
                        sx={{ textTransform: "uppercase" }}
                      >
                        {t("actions")}
                      </StyledTableCell>
                    </StyledTableRow>
                  </TableHead>
                  <TableBody>
                    {employeesLoading === "pending" ? (
                      <TableSkeleton
                        numRows={5}
                        numColumns={9 + (showMedicalColumn ? 1 : 0)}
                      />
                    ) : employeesForFilter.length === 0 ? (
                      <StyledTableRow key={"noData"}>
                        <StyledTableCell
                          colSpan={employeeTableColSpan}
                          sx={{ textAlign: "center", py: 6 }}
                        >
                          <Stack
                            alignItems="center"
                            spacing={1.5}
                            sx={{ opacity: 0.5 }}
                          >
                            <PeopleOutline
                              sx={{ fontSize: 52, color: "text.disabled" }}
                            />
                            <Typography
                              variant="body1"
                              color="text.secondary"
                              fontWeight={500}
                            >
                              {t("no_data_found")}
                            </Typography>
                          </Stack>
                        </StyledTableCell>
                      </StyledTableRow>
                    ) : (
                      employeesForFilter.map((row, index) => (
                        <StyledTableRow key={row._id}>
                          <StyledTableCell>
                            <Switch
                              checked={selectedEmployees.some(
                                (em) => em._id === row._id,
                              )}
                              onChange={() => changeCheckBox(row._id as string)}
                              inputProps={{ "aria-label": "controlled" }}
                              name={index.toString()}
                              color="secondary"
                            />
                          </StyledTableCell>
                          <StyledTableCell>
                            <Typography color="primary" variant="h6">
                              {(
                                currentPage * rowsPerPage +
                                index +
                                1
                              ).toLocaleString(
                                theme.direction === "rtl" ? "ar-SA" : "en-US",
                              )}
                            </Typography>
                          </StyledTableCell>
                          <StyledTableCell>
                            <Avatar
                              component={RouterLink}
                              alt="avatar"
                              src={row ? row.facePhoto : ""}
                              sx={{
                                cursor: "pointer",
                                height: 40,
                                width: 40,
                              }}
                              to={
                                user?.company?.type ===
                                  ClientTypes.LocalAgency &&
                                (row.status === EmployeeStatus.new ||
                                  row.status === EmployeeStatus.contracted)
                                  ? `/app/employee/${row._id}`
                                  : `/app/cvp/${row._id}`
                              }
                            />
                          </StyledTableCell>
                          <StyledTableCell
                            sx={{ color: "primary.main", fontWeight: 700 }}
                          >
                            {row.givenName + " - " + row.passportNumber}
                          </StyledTableCell>
                          <StyledTableCell
                            sx={{ color: "secondary.main", fontWeight: 700 }}
                          >
                            {te(
                              row.additionalData?.appliedCountry as string,
                            ).toUpperCase()}
                          </StyledTableCell>
                          <StyledTableCell
                            sx={{ color: "primary.main", fontWeight: 700 }}
                          >
                            {formatReferenceNumber(row.referenceNumber)}
                            {/* <Button
                          color="primary"
                          component={RouterLink}
                          to={
                            user?.company?.type === ClientTypes.LocalAgency &&
                            (row.status === EmployeeStatus.new ||
                              row.status === EmployeeStatus.contracted)
                              ? `/app/employee/${row._id}`
                              : `/app/cvp/${row._id}`
                          }
                          size="small"
                        >
                          <Stack direction="row" spacing={1}>
                            {row.additionalData?.nationality ===
                            Nationalities.ethiopian ? (
                              <img
                                alt="ethioFlag"
                                height="20px"
                                width="30px"
                                src="/ethiopiaFlag.png"
                              />
                            ) : row.additionalData?.nationality ===
                              Nationalities.kenyan ? (
                              <img
                                alt="kenyaFlag"
                                height="20px"
                                width="30px"
                                src="/kenyaFlag.png"
                              />
                            ) : row.additionalData?.nationality ===
                              Nationalities.ugandan ? (
                              <img
                                alt="ugandaFlag"
                                height="20px"
                                width="30px"
                                src="/ugandaFlag.png"
                              />
                            ) : (
                              <img
                                alt="philippineFlag"
                                height="20px"
                                width="30px"
                                src="/philippineFlag.png"
                              />
                            )}
                            <Typography
                              sx={{ fontSize: "13px", fontWeight: 700 }}
                              color="secondary"
                              textTransform="capitalize"
                            >
                              {te(row.additionalData?.nationality as string)}
                            </Typography>
                          </Stack>
                        </Button> */}
                          </StyledTableCell>
                          <StyledTableCell
                            sx={{ color: "secondary.main", fontWeight: 700 }}
                          >
                            {row.additionalData?.labourId}
                          </StyledTableCell>
                          {showMedicalColumn && (
                            <StyledTableCell>
                              {row.medicalCompleted ? (
                                <Chip
                                  label={te(
                                    row.medicalFit === false ? "unfit" : "fit",
                                  )}
                                  color={
                                    row.medicalFit === false
                                      ? "error"
                                      : "success"
                                  }
                                  size="small"
                                  sx={{ fontWeight: 600, fontSize: "0.7rem" }}
                                />
                              ) : (
                                <Chip
                                  label={te("medical_pending")}
                                  variant="outlined"
                                  size="small"
                                  sx={{ fontWeight: 600, fontSize: "0.7rem" }}
                                />
                              )}
                            </StyledTableCell>
                          )}

                          {user?.company?.type === ClientTypes.Moderator ? (
                            <>
                              <StyledTableCell
                                sx={{ color: "primary.main", fontWeight: 700 }}
                              >
                                {getDisplayName(row.owner)}
                              </StyledTableCell>

                              <StyledTableCell
                                sx={{ color: "primary.main", fontWeight: 700 }}
                              >
                                {getDisplayName(row.partner)}
                              </StyledTableCell>
                            </>
                          ) : (
                            <StyledTableCell
                              sx={{ color: "primary.main", fontWeight: 700 }}
                            >
                              {user?.company &&
                              user.company.type === ClientTypes.ForeignAgent
                                ? getDisplayName(row.owner)
                                : getDisplayName(row.partner)}
                            </StyledTableCell>
                          )}

                          {/* <StyledTableCell
                        sx={{ color: "secondary.main", fontWeight: 700 }}
                      >
                        {row.visa?.visaNumber}
                      </StyledTableCell> */}
                          {/* <StyledTableCell
                        sx={{ color: "secondary.main", fontWeight: 700 }}
                      >
                        {row.visa?.passportNumber && row.visa?.fullName
                          ? `${row.visa?.passportNumber} - ${row.visa?.fullName}`
                          : ""}
                      </StyledTableCell> */}
                          <StyledTableCell
                            sx={{ color: "primary.main", fontWeight: 700 }}
                          >
                            {format(
                              new Date(row.createdAt as Date),
                              "dd-MMM-yyyy",
                            ).toUpperCase()}
                          </StyledTableCell>
                          <StyledTableCell>
                            <Chip
                              label={te(row.status as string)}
                              color={getStatusChipColor(row.status)}
                              size="small"
                              sx={{ fontWeight: 600, fontSize: "0.7rem" }}
                            />
                          </StyledTableCell>

                          <StyledTableCell>
                            <Stack direction="row">
                              {(activeStatusTab === "available" ||
                                activeStatusTab === "selected") &&
                                isPrivilegedTransaction(
                                  user?.roles as Role[],
                                  "SelectCandidates",
                                ) && (
                                  <>
                                    {row.status === EmployeeStatus.new && (
                                      <Tooltip title={t("only_select")}>
                                        <IconButton
                                          color="secondary"
                                          onClick={() =>
                                            openAgentSelectDialog(row)
                                          }
                                          size="small"
                                        >
                                          <ThumbUp />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                    <Tooltip title={t("addAgreement")}>
                                      <IconButton
                                        color="secondary"
                                        onClick={() =>
                                          selectCandidateForVisa(row)
                                        }
                                        size="small"
                                      >
                                        <Assignment />
                                      </IconButton>
                                    </Tooltip>
                                  </>
                                )}
                              {(activeStatusTab === "available" ||
                                activeStatusTab === "selected" ||
                                activeStatusTab === "contracted") &&
                                (row.status === EmployeeStatus.selected ||
                                  row.status === EmployeeStatus.contracted) &&
                                isPrivilegedTransaction(
                                  user?.roles as Role[],
                                  "DeselectCandidates",
                                ) && (
                                  <Tooltip title={t("deselect")}>
                                    <IconButton
                                      color="secondary"
                                      onClick={() => deSelectCandidate(row)}
                                      size="small"
                                    >
                                      <ThumbDown />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              {activeStatusTab !== "available" &&
                                activeStatusTab !== "pendingInfo" &&
                                activeStatusTab !== "selected" &&
                                activeStatusTab !== "stoppedStatuses" &&
                                canOpenProcessStagesForTab(
                                  user?.roles as Role[],
                                  activeStatusTab,
                                ) && (
                                  <Tooltip title={t("status")}>
                                    <IconButton
                                      color="secondary"
                                      onClick={() => changeCandidateStatus(row)}
                                      size="small"
                                    >
                                      <NextPlan />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              {activeStatusTab === "stoppedStatuses" &&
                                isPrivilegedTransaction(
                                  user?.roles as Role[],
                                  "StopProcess",
                                ) && (
                                  <Tooltip title={t("restoreToAvailable")}>
                                    <IconButton
                                      color="success"
                                      onClick={() => restoreToAvailable(row)}
                                      size="small"
                                    >
                                      <Restore />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              {isPrivilegedTransaction(
                                user?.roles as Role[],
                                "ViewEmployees",
                              ) && (
                                <Tooltip title="Employee Detail">
                                  <IconButton
                                    color="primary"
                                    component={RouterLink}
                                    to={`/app/employee-detail/${row._id}`}
                                    size="small"
                                  >
                                    <ContactPage />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {activeStatusTab !== "pendingInfo" &&
                                isPrivilegedTransaction(
                                  user?.roles as Role[],
                                  "ViewEmployees",
                                ) && (
                                  <Tooltip title={t("view") + " " + t("cv")}>
                                    <IconButton
                                      color="primary"
                                      component={RouterLink}
                                      to={`/app/cvp/${row._id}`}
                                      size="small"
                                    >
                                      <RemoveRedEye />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              {(row.status === EmployeeStatus.new ||
                                row.status === EmployeeStatus.pending ||
                                row.status === EmployeeStatus.selected ||
                                row.status === EmployeeStatus.contracted) &&
                                isPrivilegedTransaction(
                                  user?.roles as Role[],
                                  "EditEmployees",
                                ) && (
                                  <Tooltip title={t("edit")}>
                                    <IconButton
                                      color="primary"
                                      component={RouterLink}
                                      to={`/app/employee/${row._id}`}
                                      size="small"
                                    >
                                      <Edit />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              {(activeStatusTab === "available" ||
                                activeStatusTab === "pendingInfo") &&
                                isPrivilegedTransaction(
                                  user?.roles as Role[],
                                  "StopProcess",
                                ) && (
                                  <Tooltip title={t("stopProcess")}>
                                    <IconButton
                                      color="error"
                                      onClick={() => {
                                        setStopEmployee(row);
                                        setOpenStop(true);
                                      }}
                                      size="small"
                                    >
                                      <PauseCircle />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              {(row.status === EmployeeStatus.new ||
                                row.status === EmployeeStatus.pending) &&
                                isPrivilegedTransaction(
                                  user?.roles as Role[],
                                  "DeleteEmployees",
                                ) && (
                                  <Tooltip title={t("delete")}>
                                    <IconButton
                                      color="secondary"
                                      onClick={() =>
                                        handleClickOpen(row._id as string)
                                      }
                                      size="small"
                                    >
                                      <Delete />
                                    </IconButton>
                                  </Tooltip>
                                )}
                            </Stack>
                          </StyledTableCell>
                        </StyledTableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
            <FilterSidebar listType="employees" />
          </Stack>
          <br />
          {success && <Toast severity="success">{success.message}</Toast>}
          {error && <Toast severity="error">{error.message}</Toast>}
          {employees.length > 0 && (
            <Stack spacing={1} alignItems="center" sx={{ mt: 2 }}>
              <Typography
                alignSelf="center"
                justifyContent="center"
                variant="h6"
                color="white"
                sx={{
                  backgroundColor: "primary.main",
                  p: 1,
                  borderRadius: "4px",
                }}
              >
                {t("total")}: {total}
              </Typography>

              <Paging
                total={total}
                rowsPerPage={rowsPerPage}
                currentPage={currentPage}
                setRowsPerPage={setRowsPerPage}
                setCurrentPage={setCurrentPage}
              />
            </Stack>
          )}
        </>
      </Box>

      {open && (
        <Dialog
          open={open}
          onClose={handleClose}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">{"Are you sure?"}</DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              This data will be deleted permanently.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} autoFocus>
              Cancel
            </Button>
            <Button onClick={DeleteEmployee}>Delete</Button>
          </DialogActions>
        </Dialog>
      )}

      {openChangeStatus && (
        <CustomDialog
          title={t("Change") + " " + t("status")}
          isOpen={openChangeStatus}
          handleDialogClose={dialogCloseChangeStatus}
        >
          <ProcessStages
            currentEmployee={selectedEmployee}
            handleDialogClose={dialogCloseChangeStatus}
          />
        </CustomDialog>
      )}

      {openStop && (
        <Dialog
          open={openStop}
          onClose={() => setOpenStop(false)}
          aria-labelledby="stop-dialog-title"
          aria-describedby="stop-dialog-description"
        >
          <DialogTitle id="stop-dialog-title">{t("stopProcess")}</DialogTitle>
          <DialogContent>
            <DialogContentText id="stop-dialog-description">
              {stopEmployee?.givenName} {stopEmployee?.surName}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenStop(false)} autoFocus>
              Cancel
            </Button>
            <Button color="error" onClick={handleStopEmployee}>
              {t("stopProcess")}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {openVisa && (
        <CustomDialog
          title={t("addAgreement")}
          isOpen={openVisa}
          handleDialogClose={dialogCloseVisa}
        >
          <VisaEntry
            currentEmployee={selectedEmployee}
            handleDialogClose={dialogCloseVisa}
          />
        </CustomDialog>
      )}

      {openAgentSelect && (
        <Dialog
          open={openAgentSelect}
          onClose={() => setOpenAgentSelect(false)}
          aria-labelledby="agent-select-dialog-title"
        >
          <DialogTitle id="agent-select-dialog-title">
            {t("only_select")}
          </DialogTitle>
          <DialogContent sx={{ pt: 1, minWidth: 280 }}>
            {user &&
              user.company &&
              user.company.type === ClientTypes.Moderator && (
                <CompanyFilter
                  companyType={ClientTypes.ForeignAgent}
                  setCompany={setAgentForSelect}
                  includeAll={false}
                />
              )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenAgentSelect(false)}>Cancel</Button>
            <Button
              color="secondary"
              variant="contained"
              onClick={justSelect}
              disabled={!agentForSelect}
            >
              {t("only_select")}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};
